import React, { JSX, useEffect, useRef, useState } from "react";
import { FaGithub, FaRegHeart } from "react-icons/fa"
import ReactQuill from 'react-quill-new';

import SupportModal from "./components/SupportModal";

import 'react-quill-new/dist/quill.snow.css';

export default function StoryAnalyzer() {
    const [storyText, setStoryText] = useState("");
    const [analysis, setAnalysis] = useState("Ainda não analisado...");
    const [loading, setLoading] = useState(false);
    const [parsedData, setParsedData] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const quillRef = useRef<ReactQuill | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<any>(null);

    const modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            ["blockquote", "code-block"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
        ],
    };

    const formats = [
        "header",
        "bold", "italic", "underline",
        "blockquote", "code-block",
        "list", "bullet",
        "link",
    ];

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (storyText.trim().length > 0) {
                analyzeStory(storyText);
            }
        }, 1000);
        return () => clearTimeout(timerRef.current);
    }, [storyText]);

    const extractJsonData = (text: string) => {
        const match = text.match(/\[\[\s*({[\s\S]*?})\s*\]\]/);
        if (match) {
            try {
                return JSON.parse(match[1]);
            } catch (e) {
                console.error("Erro ao fazer parse do JSON:", e);
                return null;
            }
        }
        return null;
    };

    const analyzeStory = async (text: string) => {
        setLoading(true);
        try {
            const response = await fetch(
                "https://api-ias-routes.vercel.app/getAnalyzedBook",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        text: text
                    }),
                }
            );
            const data = await response.json();
            const responseText = data.candidates?.[0]?.content?.parts?.map((p: { text: string }) => p.text).join("\n") || "Sem resposta da IA.";
            setAnalysis(responseText);
            const jsonData = extractJsonData(responseText);
            setParsedData(jsonData);
        } catch (error) {
            setAnalysis("Erro ao conectar com a IA.");
        }
        setLoading(false);
    };




    const highlightInQuill = (targetText: string) => {
        if (!quillRef.current) return;
        const editor = quillRef.current.getEditor();
        const text = editor.getText();
        const index = text.indexOf(targetText);
        if (index === -1) return;
        editor.setSelection(index, targetText.length);
        const bounds = editor.getBounds(index, targetText.length);
        if (containerRef.current && bounds) {
            containerRef.current.scrollTo({
                top: bounds.top - 20,
                behavior: "smooth",
            });
        }
    };

    const renderAnalysisWithLinks = (text: string) => {
        const cleanText = text.replace(/\[\[\s*{[\s\S]*?}\s*\]\]/g, "").trim();
        const linkRegex = /<<(.+?)>>/g;
        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(cleanText)) !== null) {
            const before = cleanText.substring(lastIndex, match.index);
            parts.push(...renderBoldText(before)); // trata negrito antes do link

            const linkText = match[1];
            parts.push(
                <button
                    key={match.index}
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={() => highlightInQuill(linkText)}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                    {linkText}
                </button>
            );

            lastIndex = linkRegex.lastIndex;
        }

        parts.push(...renderBoldText(cleanText.substring(lastIndex))); // trata o restante

        return parts;
    };

    // Função auxiliar para converter **negrito**
    const renderBoldText = (text: string): (string | JSX.Element)[] => {
        const boldRegex = /\*\*(.+?)\*\*/g;
        const result: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;

        while ((match = boldRegex.exec(text)) !== null) {
            const before = text.substring(lastIndex, match.index);
            const boldContent = match[1];
            result.push(before);
            result.push(<strong key={match.index}>{boldContent}</strong>);
            lastIndex = boldRegex.lastIndex;
        }

        result.push(text.substring(lastIndex));
        return result;
    };

    const getColorFromScore = (score: number) => {
        const r = Math.round(255 - (score * 2.55));
        const g = Math.round(score * 2.55);
        return `rgba(${r}, ${g}, 0, 0.70)`;
    };

    const renderScoreBubbles = (data: any) => {
        if (!data) return null;
        const desempenho = data.indicador_desempenho || {};
        const emocao = data.indicador_emocao || {};

        const renderGroup = (group: any, title: string) => (
            <div>
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <div className="flex flex-wrap gap-3">
                    {Object.entries(group).map(([key, value]) => {
                        const score = parseInt(value as string);
                        return (
                            <div key={key} className="flex flex-col items-center text-xs w-20">
                                <div
                                    className="w-6 h-6 rounded-full"
                                    style={{
                                        backgroundColor: getColorFromScore(score),
                                        fontWeight: "800",
                                        textAlign: "center",
                                        paddingTop: "3px",
                                        color: "white",
                                        border: "1px solid #00000040",
                                        textShadow: "1px 1px 0 dark-gray, -1px -1px 0 gray, 1px -1px 0 gray, -1px 1px 0 gray",
                                    }}
                                    title={`${key.replace(/_/g, " ")}: ${score}`}

                                >
                                    {score}
                                </div>
                                <span className="mt-1 text-center break-words">{key.replace(/_/g, " ")}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );

        return (
            <div className="mt-1 bg-white p-4 rounded-lg shadow">
                {renderGroup(desempenho, "Desempenho e Público")}
                {renderGroup(emocao, "Elementos de Emoção")}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-screen">
            <div className="flex flex-1">
                {/* Painel da IA */}
                <div className="w-1/3 p-4 border-r bg-gray-100 overflow-y-auto" style={{ maxHeight: "100vh" }}>
                    <div className="bg-white  shadow-md rounded-lg p-4">
                        <h2 className="text-xl font-bold mb-2">Análise da IA</h2>
                        {loading ? (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg
                                    className="animate-spin h-5 w-5 mr-2 text-gray-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v8H4z"
                                    />
                                </svg>
                                Analisando...
                            </div>
                        ) : (
                            <div className="text-sm whitespace-pre-line">
                                {renderAnalysisWithLinks(analysis)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Editor de História com ReactQuill */}
                <div className="w-2/3 p-6 overflow-y-auto" ref={containerRef}>
                    <h1 className="text-2xl font-semibold mb-4">Escreva sua história</h1>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={storyText}
                        onChange={setStoryText}
                        modules={modules}
                        formats={formats}
                        placeholder="Digite sua história aqui..."
                        style={{ height: "48vh" }}
                        className="custom-quill"
                    />

                    {/* Bolinhas de desempenho e emoção */}
                    {renderScoreBubbles(parsedData)}
                </div>
            </div>

            {/* Ícone do GitHub */}
            <div className="w-full flex justify-center py-2 gap-x-6">
                <a
                    href="https://github.com/azebuji"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-black transition"
                >
                    <FaGithub size={20} />
                </a>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setIsModalOpen(true);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-purple-600 hover:text-purple-800 transition"
                >
                    <FaRegHeart className="mr-2" />
                    Gostou? gostaria de me apoiar?
                </a>
            </div>
            <SupportModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
}
